import React, { useEffect, useState } from 'react';

const CATEGORY_THEMES = {
  life_skills: { primary: '#10b981', light: '#d1fae5', dark: '#065f46', label: 'Life Skills' },
  safety: { primary: '#ef4444', light: '#fee2e2', dark: '#991b1b', label: 'Safety' },
  financial: { primary: '#f59e0b', light: '#fef3c7', dark: '#92400e', label: 'Financial' },
  career: { primary: '#8b5cf6', light: '#ede9fe', dark: '#5b21b6', label: 'Career' },
  environment: { primary: '#06b6d4', light: '#cffafe', dark: '#0e7490', label: 'Environment' },
  competitive_exams: { primary: '#f97316', light: '#ffedd5', dark: '#9a3412', label: 'Competitive Exams' },
};

// Determines the character activity based on chapter title keywords
function getActivity(chapterTitle, category) {
  const t = (chapterTitle || '').toLowerCase();
  if (t.includes('introduc')) return 'waving';
  if (t.includes('practice') || t.includes('exercise')) return 'exercising';
  if (t.includes('exam') || t.includes('assessment') || t.includes('quiz')) return 'studying';
  if (t.includes('summary') || t.includes('review')) return 'reading';
  if (t.includes('plan') || t.includes('goal') || t.includes('roadmap') || t.includes('action plan')) return 'thinking';
  if (t.includes('safety') || t.includes('protect') || t.includes('emergency')) return 'shielding';
  if (t.includes('invest') || t.includes('money') || t.includes('finance') || t.includes('tax') || t.includes('budget')) return 'counting';
  if (t.includes('career') || t.includes('job') || t.includes('resume') || t.includes('interview') || t.includes('entrepreneur')) return 'presenting';
  if (t.includes('plant') || t.includes('water') || t.includes('environment') || t.includes('sustain')) return 'planting';
  if (t.includes('nda') || t.includes('defence') || t.includes('sainik') || t.includes('armed forces') || t.includes('military')) return 'shielding';
  if (t.includes('jee') || t.includes('neet') || t.includes('clat') || t.includes('competitive') || t.includes('entrance') || t.includes('after 10') || t.includes('after 12')) return 'studying';
  // Category defaults
  if (category === 'safety') return 'shielding';
  if (category === 'financial') return 'counting';
  if (category === 'career') return 'presenting';
  if (category === 'environment') return 'planting';
  if (category === 'competitive_exams') return 'studying';
  return 'reading';
}

// Each activity renders a different scene with the character
const ActivityScene = ({ activity, theme, chapterNum }) => {
  const { primary, light, dark } = theme;

  const animations = `
    @keyframes charFloat {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-8px); }
    }
    @keyframes charWave {
      0%, 100% { transform: rotate(-15deg); }
      50% { transform: rotate(15deg); }
    }
    @keyframes pulseSlow {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.1); opacity: 0.8; }
    }
    @keyframes coinSpin {
      0% { transform: translateY(0) rotate(0deg); opacity: 1; }
      100% { transform: translateY(-40px) rotate(360deg); opacity: 0; }
    }
    @keyframes plantGrow {
      0% { transform: scaleY(0.3); transform-origin: bottom; opacity: 0.5; }
      100% { transform: scaleY(1); transform-origin: bottom; opacity: 1; }
    }
    @keyframes thinkBubble {
      0%, 100% { opacity: 0.3; transform: scale(0.9); }
      50% { opacity: 1; transform: scale(1); }
    }
    @keyframes starTwinkle {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.3; transform: scale(0.7); }
    }
    @keyframes bgRotate {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes slideUp {
      0% { transform: translateY(6px); opacity: 0; }
      100% { transform: translateY(0); opacity: 1; }
    }
  `;

  // Base person SVG parts - color customized
  const person = (x, y, outfit = primary) => (
    <g style={{ animation: 'charFloat 3s ease-in-out infinite' }}>
      {/* Shadow */}
      <ellipse cx={x} cy={y + 55} rx={18} ry={5} fill="rgba(0,0,0,0.15)" />
      {/* Body */}
      <rect x={x - 14} y={y + 14} width={28} height={30} rx={8} fill={outfit} />
      {/* Head */}
      <circle cx={x} cy={y} r={16} fill="#FDDCB5" />
      {/* Eyes */}
      <circle cx={x - 5} cy={y - 2} r={2.5} fill="#333" />
      <circle cx={x + 5} cy={y - 2} r={2.5} fill="#333" />
      {/* Eye shine */}
      <circle cx={x - 4} cy={y - 3} r={1} fill="white" />
      <circle cx={x + 6} cy={y - 3} r={1} fill="white" />
      {/* Smile */}
      <path d={`M${x - 5} ${y + 5} Q${x} ${y + 9} ${x + 5} ${y + 5}`} stroke="#a0522d" strokeWidth={1.5} fill="none" strokeLinecap="round" />
      {/* Hair */}
      <path d={`M${x - 15} ${y - 6} Q${x - 16} ${y - 18} ${x} ${y - 17} Q${x + 16} ${y - 18} ${x + 15} ${y - 6}`} fill="#4a3728" />
      {/* Legs */}
      <rect x={x - 10} y={y + 42} width={8} height={14} rx={4} fill={dark} />
      <rect x={x + 2} y={y + 42} width={8} height={14} rx={4} fill={dark} />
    </g>
  );

  if (activity === 'waving') {
    return (
      <svg viewBox="0 0 200 180" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxHeight: 180 }}>
        <style>{animations}</style>
        {/* Background circle */}
        <circle cx={100} cy={90} r={75} fill={light} opacity={0.5} />
        {/* Stars */}
        {[30, 160, 20, 170, 100].map((cx, i) => (
          <text key={i} x={cx} y={[30, 25, 140, 150, 15][i]} fontSize={14} style={{ animation: `starTwinkle ${1.2 + i * 0.3}s ease-in-out infinite` }}>⭐</text>
        ))}
        {/* Character */}
        {person(100, 70, primary)}
        {/* Left arm - static */}
        <line x1={86} y1={85} x2={72} y2={100} stroke={primary} strokeWidth={7} strokeLinecap="round" />
        {/* Right arm - waving */}
        <g style={{ transformOrigin: '114px 82px', animation: 'charWave 1s ease-in-out infinite' }}>
          <line x1={114} y1={82} x2={130} y2={65} stroke={primary} strokeWidth={7} strokeLinecap="round" />
          <circle cx={130} cy={65} r={5} fill="#FDDCB5" />
        </g>
        {/* Speech bubble */}
        <g style={{ animation: 'thinkBubble 2s ease-in-out infinite' }}>
          <rect x={120} y={40} width={55} height={24} rx={10} fill="white" stroke={primary} strokeWidth={1.5} />
          <polygon points="122,60 130,60 125,68" fill="white" stroke={primary} strokeWidth={1.5} />
          <text x={127} y={56} fontSize={10} fill={dark} fontWeight="600">Hello! 👋</text>
        </g>
        {/* Chapter number badge */}
        <circle cx={165} cy={155} r={18} fill={primary} />
        <text x={165} y={160} textAnchor="middle" fontSize={11} fill="white" fontWeight="bold">Ch.{chapterNum}</text>
      </svg>
    );
  }

  if (activity === 'reading') {
    return (
      <svg viewBox="0 0 200 180" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxHeight: 180 }}>
        <style>{animations}</style>
        <circle cx={100} cy={90} r={75} fill={light} opacity={0.5} />
        {/* Open book */}
        <g style={{ transformOrigin: '100px 110px', animation: 'pulseSlow 2.5s ease-in-out infinite' }}>
          <path d="M70 105 Q100 100 130 105 L130 130 Q100 125 70 130 Z" fill={dark} opacity={0.9} />
          <path d="M70 105 L70 130 Q100 125 100 125 L100 100 Z" fill="white" stroke={primary} strokeWidth={1} />
          <path d="M130 105 L130 130 Q100 125 100 125 L100 100 Z" fill="white" stroke={primary} strokeWidth={1} />
          {/* Book lines */}
          <line x1={75} y1={111} x2={97} y2={109} stroke={primary} strokeWidth={1} opacity={0.6} />
          <line x1={75} y1={116} x2={97} y2={114} stroke={primary} strokeWidth={1} opacity={0.6} />
          <line x1={75} y1={121} x2={97} y2={119} stroke={primary} strokeWidth={1} opacity={0.6} />
          <line x1={103} y1={109} x2={125} y2={111} stroke={primary} strokeWidth={1} opacity={0.6} />
          <line x1={103} y1={114} x2={125} y2={116} stroke={primary} strokeWidth={1} opacity={0.6} />
          <line x1={103} y1={119} x2={125} y2={121} stroke={primary} strokeWidth={1} opacity={0.6} />
        </g>
        {/* Character */}
        {person(100, 60, primary)}
        {/* Arms holding book */}
        <line x1={86} y1={78} x2={76} y2={103} stroke={primary} strokeWidth={7} strokeLinecap="round" />
        <line x1={114} y1={78} x2={124} y2={103} stroke={primary} strokeWidth={7} strokeLinecap="round" />
        {/* Thought bubbles */}
        <circle cx={135} cy={45} r={4} fill={primary} opacity={0.5} style={{ animation: 'thinkBubble 1.5s ease-in-out infinite 0.2s' }} />
        <circle cx={145} cy={35} r={6} fill={primary} opacity={0.6} style={{ animation: 'thinkBubble 1.5s ease-in-out infinite 0.5s' }} />
        <circle cx={157} cy={25} r={9} fill={primary} opacity={0.7} style={{ animation: 'thinkBubble 1.5s ease-in-out infinite 0.8s' }} />
        <text x={150} y={29} textAnchor="middle" fontSize={10}>💡</text>
        {/* Chapter badge */}
        <circle cx={165} cy={155} r={18} fill={primary} />
        <text x={165} y={160} textAnchor="middle" fontSize={11} fill="white" fontWeight="bold">Ch.{chapterNum}</text>
      </svg>
    );
  }

  if (activity === 'studying') {
    return (
      <svg viewBox="0 0 200 180" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxHeight: 180 }}>
        <style>{animations}</style>
        <circle cx={100} cy={90} r={75} fill={light} opacity={0.5} />
        {/* Desk */}
        <rect x={55} y={120} width={90} height={8} rx={3} fill={dark} opacity={0.8} />
        <rect x={65} y={128} width={6} height={20} rx={2} fill={dark} opacity={0.6} />
        <rect x={129} y={128} width={6} height={20} rx={2} fill={dark} opacity={0.6} />
        {/* Laptop */}
        <rect x={72} y={103} width={56} height={18} rx={3} fill="#334155" />
        <rect x={68} y={119} width={64} height={4} rx={2} fill="#475569" />
        {/* Screen glow */}
        <rect x={74} y={105} width={52} height={14} rx={2} fill="#0ea5e9" opacity={0.8} />
        <text x={100} y={115} textAnchor="middle" fontSize={8} fill="white">&lt;code/&gt;</text>
        {/* Character seated */}
        <g style={{ animation: 'charFloat 3s ease-in-out infinite' }}>
          <ellipse cx={100} cy={158} rx={15} ry={4} fill="rgba(0,0,0,0.12)" />
          <circle cx={100} cy={70} r={16} fill="#FDDCB5" />
          <rect x={86} y={84} width={28} height={22} rx={8} fill={primary} />
          {/* Eyes focused on screen */}
          <ellipse cx={95} cy={68} rx={2.5} ry={2} fill="#333" />
          <ellipse cx={105} cy={68} rx={2.5} ry={2} fill="#333" />
          <circle cx={96} cy={67} r={0.8} fill="white" />
          <circle cx={106} cy={67} r={0.8} fill="white" />
          <path d={`M 95 76 Q 100 79 105 76`} stroke="#a0522d" strokeWidth={1.5} fill="none" strokeLinecap="round" />
          <path d={`M 85 86 Q 84 98 72 115`} stroke={primary} strokeWidth={7} strokeLinecap="round" fill="none" />
          <path d={`M 115 86 Q 116 98 128 115`} stroke={primary} strokeWidth={7} strokeLinecap="round" fill="none" />
          {/* Hair */}
          <path d={`M 85 64 Q 84 53 100 52 Q 116 53 115 64`} fill="#4a3728" />
        </g>
        {/* Floating stars for "exam mode" */}
        <text x={40} y={55} fontSize={14} style={{ animation: 'starTwinkle 1s ease-in-out infinite' }}>⭐</text>
        <text x={155} y={55} fontSize={14} style={{ animation: 'starTwinkle 1.3s ease-in-out infinite 0.3s' }}>⭐</text>
        <text x={160} y={85} fontSize={12} style={{ animation: 'starTwinkle 1.1s ease-in-out infinite 0.6s' }}>✨</text>
        <circle cx={165} cy={155} r={18} fill={primary} />
        <text x={165} y={160} textAnchor="middle" fontSize={11} fill="white" fontWeight="bold">Ch.{chapterNum}</text>
      </svg>
    );
  }

  if (activity === 'thinking') {
    return (
      <svg viewBox="0 0 200 180" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxHeight: 180 }}>
        <style>{animations}</style>
        <circle cx={100} cy={90} r={75} fill={light} opacity={0.5} />
        {person(95, 65, primary)}
        {/* Left arm - hand on chin */}
        <line x1={81} y1={80} x2={76} y2={95} stroke={primary} strokeWidth={7} strokeLinecap="round" />
        <circle cx={76} cy={97} r={5} fill="#FDDCB5" />
        {/* Right arm - behind back */}
        <line x1={109} y1={80} x2={118} y2={95} stroke={primary} strokeWidth={7} strokeLinecap="round" />
        {/* Thought cloud */}
        <g style={{ animation: 'thinkBubble 2s ease-in-out infinite' }}>
          <circle cx={125} cy={48} r={5} fill="white" stroke={primary} strokeWidth={1} />
          <circle cx={135} cy={38} r={7} fill="white" stroke={primary} strokeWidth={1} />
          <circle cx={148} cy={30} r={10} fill="white" stroke={primary} strokeWidth={1} />
          <circle cx={162} cy={28} r={12} fill="white" stroke={primary} strokeWidth={1} />
          <circle cx={155} cy={40} r={8} fill="white" stroke={primary} strokeWidth={1} />
          <text x={154} y={32} textAnchor="middle" fontSize={13}>🎯</text>
        </g>
        <circle cx={165} cy={155} r={18} fill={primary} />
        <text x={165} y={160} textAnchor="middle" fontSize={11} fill="white" fontWeight="bold">Ch.{chapterNum}</text>
      </svg>
    );
  }

  if (activity === 'shielding') {
    return (
      <svg viewBox="0 0 200 180" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxHeight: 180 }}>
        <style>{animations}</style>
        <circle cx={100} cy={90} r={75} fill={light} opacity={0.5} />
        {/* Shield behind character */}
        <g style={{ animation: 'pulseSlow 2s ease-in-out infinite' }}>
          <path d="M65 75 Q65 45 100 40 Q135 45 135 75 Q135 105 100 120 Q65 105 65 75 Z" fill={primary} opacity={0.25} stroke={primary} strokeWidth={2} />
          <path d="M72 77 Q72 52 100 47 Q128 52 128 77 Q128 100 100 113 Q72 100 72 77 Z" fill="white" opacity={0.4} />
          <text x={100} y={82} textAnchor="middle" fontSize={22}>🛡️</text>
        </g>
        {person(100, 65, primary)}
        {/* Arms extended with shield */}
        <line x1={86} y1={78} x2={72} y2={92} stroke={primary} strokeWidth={7} strokeLinecap="round" />
        <line x1={114} y1={78} x2={128} y2={92} stroke={primary} strokeWidth={7} strokeLinecap="round" />
        {/* Floating protection icons */}
        <text x={35} y={60} fontSize={16} style={{ animation: 'starTwinkle 1.2s ease-in-out infinite' }}>✅</text>
        <text x={150} y={60} fontSize={16} style={{ animation: 'starTwinkle 1.5s ease-in-out infinite 0.4s' }}>✅</text>
        <circle cx={165} cy={155} r={18} fill={primary} />
        <text x={165} y={160} textAnchor="middle" fontSize={11} fill="white" fontWeight="bold">Ch.{chapterNum}</text>
      </svg>
    );
  }

  if (activity === 'counting') {
    return (
      <svg viewBox="0 0 200 180" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxHeight: 180 }}>
        <style>{animations}</style>
        <circle cx={100} cy={90} r={75} fill={light} opacity={0.5} />
        {/* Floating coins */}
        {[60, 90, 120, 75, 110].map((x, i) => (
          <text key={i} x={x} y={30 + i * 8} fontSize={16}
            style={{ animation: `coinSpin ${1.5 + i * 0.4}s ease-in-out infinite ${i * 0.3}s` }}>
            💰
          </text>
        ))}
        {person(100, 70, primary)}
        {/* Left arm up holding coins */}
        <line x1={86} y1={78} x2={70} y2={62} stroke={primary} strokeWidth={7} strokeLinecap="round" />
        <circle cx={68} cy={60} r={6} fill="#FDDCB5" />
        {/* Coin in hand */}
        <text x={58} y={55} fontSize={14} style={{ animation: 'pulseSlow 1.5s ease-in-out infinite' }}>💵</text>
        {/* Right arm out */}
        <line x1={114} y1={78} x2={128} y2={90} stroke={primary} strokeWidth={7} strokeLinecap="round" />
        <circle cx={165} cy={155} r={18} fill={primary} />
        <text x={165} y={160} textAnchor="middle" fontSize={11} fill="white" fontWeight="bold">Ch.{chapterNum}</text>
      </svg>
    );
  }

  if (activity === 'presenting') {
    return (
      <svg viewBox="0 0 200 180" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxHeight: 180 }}>
        <style>{animations}</style>
        <circle cx={100} cy={90} r={75} fill={light} opacity={0.5} />
        {/* Presentation board */}
        <g style={{ animation: 'pulseSlow 3s ease-in-out infinite' }}>
          <rect x={115} y={45} width={60} height={45} rx={4} fill="white" stroke={primary} strokeWidth={2} />
          <line x1={125} y1={56} x2={165} y2={56} stroke={primary} strokeWidth={2} opacity={0.5} />
          <line x1={125} y1={63} x2={158} y2={63} stroke={primary} strokeWidth={2} opacity={0.5} />
          <line x1={125} y1={70} x2={162} y2={70} stroke={primary} strokeWidth={2} opacity={0.5} />
          <path d="M 130 76 L 138 84 L 155 68" stroke="#10b981" strokeWidth={2.5} fill="none" strokeLinecap="round" />
          <line x1={145} y1={90} x2={145} y2={105} stroke={primary} strokeWidth={2} />
          <path d="M 135 105 L 145 105 L 155 105" stroke={primary} strokeWidth={2} />
        </g>
        {person(80, 70, primary)}
        {/* Pointing arm */}
        <line x1={94} y1={82} x2={112} y2={65} stroke={primary} strokeWidth={7} strokeLinecap="round" />
        <circle cx={114} cy={63} r={5} fill="#FDDCB5" />
        {/* Left arm by side */}
        <line x1={66} y1={82} x2={60} y2={100} stroke={primary} strokeWidth={7} strokeLinecap="round" />
        {/* Rising arrows */}
        <text x={30} y={60} fontSize={14} style={{ animation: 'slideUp 1.5s ease-in-out infinite' }}>📈</text>
        <circle cx={165} cy={155} r={18} fill={primary} />
        <text x={165} y={160} textAnchor="middle" fontSize={11} fill="white" fontWeight="bold">Ch.{chapterNum}</text>
      </svg>
    );
  }

  if (activity === 'planting') {
    return (
      <svg viewBox="0 0 200 180" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxHeight: 180 }}>
        <style>{animations}</style>
        <circle cx={100} cy={90} r={75} fill={light} opacity={0.5} />
        {/* Ground */}
        <ellipse cx={100} cy={145} rx={60} ry={8} fill={dark} opacity={0.3} />
        {/* Growing plant */}
        <g style={{ transformOrigin: '130px 145px', animation: 'plantGrow 3s ease-in-out infinite alternate' }}>
          <line x1={130} y1={145} x2={130} y2={105} stroke="#16a34a" strokeWidth={4} />
          <ellipse cx={120} cy={122} rx={12} ry={8} fill="#22c55e" transform="rotate(-20, 120, 122)" />
          <ellipse cx={140} cy={115} rx={12} ry={8} fill="#4ade80" transform="rotate(20, 140, 115)" />
          <ellipse cx={130} cy={105} rx={10} ry={7} fill="#86efac" />
        </g>
        {/* Flower */}
        <text x={124} y={100} fontSize={14} style={{ animation: 'pulseSlow 2s ease-in-out infinite' }}>🌸</text>
        {/* Watering can */}
        <text x={55} y={108} fontSize={22} style={{ animation: 'charWave 2s ease-in-out infinite' }}>🪣</text>
        {/* Water drops */}
        <circle cx={88} cy={90} r={2} fill="#60a5fa" opacity={0.8} style={{ animation: 'slideUp 1s ease-in-out infinite' }} />
        <circle cx={93} cy={95} r={2.5} fill="#60a5fa" opacity={0.8} style={{ animation: 'slideUp 1s ease-in-out infinite 0.3s' }} />
        <circle cx={85} cy={100} r={2} fill="#60a5fa" opacity={0.8} style={{ animation: 'slideUp 1s ease-in-out infinite 0.6s' }} />
        {person(75, 70, primary)}
        {/* Arms extended holding can */}
        <line x1={89} y1={82} x2={68} y2={98} stroke={primary} strokeWidth={7} strokeLinecap="round" />
        <line x1={61} y1={80} x2={68} y2={98} stroke={primary} strokeWidth={7} strokeLinecap="round" />
        <circle cx={165} cy={155} r={18} fill={primary} />
        <text x={165} y={160} textAnchor="middle" fontSize={11} fill="white" fontWeight="bold">Ch.{chapterNum}</text>
      </svg>
    );
  }

  if (activity === 'exercising') {
    return (
      <svg viewBox="0 0 200 180" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxHeight: 180 }}>
        <style>{animations}</style>
        <circle cx={100} cy={90} r={75} fill={light} opacity={0.5} />
        {/* Dumbbell */}
        <g style={{ animation: 'charWave 1.5s ease-in-out infinite', transformOrigin: '130px 85px' }}>
          <rect x={118} y={83} width={26} height={6} rx={3} fill={dark} />
          <rect x={115} y={79} width={8} height={14} rx={3} fill={primary} />
          <rect x={137} y={79} width={8} height={14} rx={3} fill={primary} />
        </g>
        {person(100, 65, primary)}
        {/* Right arm raised with dumbbell */}
        <line x1={114} y1={76} x2={126} y2={90} stroke={primary} strokeWidth={7} strokeLinecap="round" />
        {/* Left arm by side */}
        <line x1={86} y1={76} x2={74} y2={92} stroke={primary} strokeWidth={7} strokeLinecap="round" />
        {/* Sweat drops */}
        <text x={42} y={55} fontSize={12} style={{ animation: 'slideUp 1s ease-in-out infinite' }}>💪</text>
        <text x={145} y={42} fontSize={12} style={{ animation: 'starTwinkle 1.2s ease-in-out infinite' }}>✨</text>
        <circle cx={165} cy={155} r={18} fill={primary} />
        <text x={165} y={160} textAnchor="middle" fontSize={11} fill="white" fontWeight="bold">Ch.{chapterNum}</text>
      </svg>
    );
  }

  // Default: waving
  return (
    <svg viewBox="0 0 200 180" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxHeight: 180 }}>
      <style>{animations}</style>
      <circle cx={100} cy={90} r={75} fill={light} opacity={0.5} />
      {person(100, 70, primary)}
      <line x1={86} y1={82} x2={72} y2={96} stroke={primary} strokeWidth={7} strokeLinecap="round" />
      <g style={{ transformOrigin: '114px 80px', animation: 'charWave 1s ease-in-out infinite' }}>
        <line x1={114} y1={80} x2={130} y2={65} stroke={primary} strokeWidth={7} strokeLinecap="round" />
      </g>
      <circle cx={165} cy={155} r={18} fill={primary} />
      <text x={165} y={160} textAnchor="middle" fontSize={11} fill="white" fontWeight="bold">Ch.{chapterNum}</text>
    </svg>
  );
};

export function ChapterCharacter({ category, chapterTitle, chapterNum, chapterContent }) {
  const theme = CATEGORY_THEMES[category] || CATEGORY_THEMES.life_skills;
  const activity = getActivity(chapterTitle, category);

  // Extract key tips from markdown content (bold lines or bullet points)
  const tips = React.useMemo(() => {
    if (!chapterContent) return [];
    const extracted = [];
    const lines = chapterContent.split('\n');
    for (const line of lines) {
      // Extract bold phrases: **text**
      const boldMatches = line.match(/\*\*([^*]{15,60})\*\*/g);
      if (boldMatches) {
        boldMatches.forEach(m => {
          const txt = m.replace(/\*\*/g, '').trim();
          if (txt.length >= 15 && !extracted.includes(txt)) extracted.push(txt);
        });
      }
    }
    // Fallback tips if nothing extracted
    if (extracted.length === 0) {
      return ['Read carefully', 'Take notes on key points', 'Think about applications', 'Discuss with peers'];
    }
    return extracted.slice(0, 5);
  }, [chapterContent]);

  const [tipIndex, setTipIndex] = React.useState(0);
  const [tipVisible, setTipVisible] = React.useState(true);

  // Cycle tips every 4 seconds
  React.useEffect(() => {
    if (tips.length <= 1) return;
    const interval = setInterval(() => {
      setTipVisible(false);
      setTimeout(() => {
        setTipIndex(i => (i + 1) % tips.length);
        setTipVisible(true);
      }, 400);
    }, 4000);
    return () => clearInterval(interval);
  }, [tips]);

  const currentTip = tips[tipIndex] || '';

  return (
    <div style={{
      background: `linear-gradient(135deg, ${theme.light} 0%, white 60%)`,
      borderRadius: '1rem',
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '0.5rem',
      border: `1.5px solid ${theme.primary}30`,
    }}>
      {/* Cycling tip bubble above the character */}
      <div style={{
        width: '100%',
        minHeight: '40px',
        background: 'white',
        border: `1.5px solid ${theme.primary}40`,
        borderRadius: '0.75rem',
        padding: '0.4rem 0.75rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        transition: 'opacity 0.4s ease',
        opacity: tipVisible ? 1 : 0,
      }}>
        <span style={{ fontSize: '0.7rem', color: theme.primary, fontWeight: 700, flexShrink: 0 }}>
          TIP {tipIndex + 1}/{tips.length}
        </span>
        <span style={{ fontSize: '0.7rem', color: theme.dark, fontWeight: 500, lineHeight: 1.3 }}>
          {currentTip}
        </span>
      </div>

      <ActivityScene activity={activity} theme={theme} chapterNum={chapterNum} />
      <p style={{ fontSize: '0.75rem', color: theme.dark, fontWeight: 600, textAlign: 'center', margin: 0 }}>
        {activity === 'waving' && 'Getting started!'}
        {activity === 'reading' && 'Dive in and learn!'}
        {activity === 'studying' && 'Exam prep mode!'}
        {activity === 'thinking' && 'Plan your goals!'}
        {activity === 'shielding' && 'Stay protected!'}
        {activity === 'counting' && 'Manage wisely!'}
        {activity === 'presenting' && 'Level up your career!'}
        {activity === 'planting' && 'Grow sustainably!'}
        {activity === 'exercising' && 'Practice makes perfect!'}
      </p>
    </div>
  );
}
